import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { autoRegisterIfPermitted, enablePushNotifications } from "@/lib/push";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  // Skip /auth/me during Google callback so AuthCallback can set the session first.
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash.includes("session_id=")) {
      setLoading(false);
    }
  }, []);

  // If the user is already authenticated AND has previously granted notification
  // permission, silently refresh the FCM token on the backend.
  useEffect(() => {
    if (user) autoRegisterIfPermitted();
  }, [user]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("cw_token", data.token);
    setUser(data.user);
    // best-effort silent push registration after login
    autoRegisterIfPermitted();
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.token) localStorage.setItem("cw_token", data.token);
    setUser(data.user);
    autoRegisterIfPermitted();
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("cw_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh: fetchMe, enablePush: enablePushNotifications }}>
      {children}
    </AuthContext.Provider>
  );
};
